// Story 1.22 — TanStack Query hook for GET /api/v1/creators/me.
//
// Drives the CreatorProfilePage's status-driven render. The 403 cases
// (NotApproved | Suspended) surface as caught errors carrying the
// snake_case reason discriminator so the page renders the right card.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type LinkedAccount = {
  kind: string;
  url: string;
  verified_at?: string;
};

export type CreatorProfile = {
  user_id: string;
  display_name: string | null;
  hello_text: string | null;
  bio: string | null;
  image_signed_url: string | null;
  image_signed_url_expires_at: string | null;
  linked_accounts: LinkedAccount[] | null;
  region: string | null;
  language: string | null;
  /** Consent toggle: TRUE = admins may not add this creator to
   *  portfolios without an application. */
  portfolio_auto_assign_opt_out: boolean;
  creator_approved_at: string | null;
  profile_updated_at: string | null;
  /** Story 1.34 — standing against the Creator Program terms.
   *  `"needs_acceptance"` means creator-only WRITE paths will refuse with
   *  428 CREATOR_TOS_ACCEPTANCE_REQUIRED until accepted. Reads are never
   *  gated, and the user's session is never gated on this. */
  creator_tos_status: "current" | "needs_acceptance";
  creator_tos_version_id: string | null;
  creator_tos_version_label: string | null;
};

export type CreatorProfileEnvelope = { data: CreatorProfile };

export type CreatorProfileFetchError = {
  code: string;
  message: string;
  reason?: "not_approved" | "suspended";
  status: number;
};

async function fetchCreatorProfile(): Promise<CreatorProfileEnvelope> {
  const response = await fetch("/api/v1/creators/me", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: CreatorProfileFetchError = {
      code: "CREATOR_PROFILE_FETCH_FAILED",
      message: `Profile fetch failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          reason: raw.error.reason,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  return (await response.json()) as CreatorProfileEnvelope;
}

export function useCreatorProfile() {
  return useQuery<CreatorProfileEnvelope, CreatorProfileFetchError>({
    queryKey: ["creator-profile-me"],
    queryFn: fetchCreatorProfile,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}
